/**
 * ComingSoonPage
 *
 * Route: /coming-soon
 * Full-screen cinematic coming soon page with countdown timer and email capture.
 * Launch date read from platform_settings key `coming_soon_launch_date`.
 * Falls back to 90 days from now if not set.
 */
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { subscribeNewsletter } from '../services/newsletterService';

const GOLD    = '#d39a21';
const GOLD2   = '#8a6d1b';
const BG      = '#0a0807';
const WHITE   = '#f0ede6';
const MUTED   = 'rgba(240,237,230,0.72)';
const GD      = "'Gilda Display', serif";
const NU      = "'Nunito', sans-serif";

// ── Countdown hook ────────────────────────────────────────────────────────────
function useCountdown(targetDate) {
  const calc = () => {
    const diff = Math.max(0, new Date(targetDate) - Date.now());
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
      done:    diff === 0,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return t;
}

// ── Animated digit ────────────────────────────────────────────────────────────
function Digit({ value, label }) {
  const str = String(value).padStart(2, '0');
  return (
    <div style={{ textAlign: 'center', minWidth: 72 }}>
      <div style={{
        fontFamily:   GD,
        fontSize:     'clamp(40px, 8vw, 72px)',
        fontWeight:   400,
        color:        GOLD,
        lineHeight:   1,
        letterSpacing: '-0.02em',
        transition:   'opacity 0.15s',
      }}>
        {str}
      </div>
      <div style={{
        fontFamily:    NU,
        fontSize:      10,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color:         MUTED,
        marginTop:     8,
        fontWeight:    600,
      }}>
        {label}
      </div>
    </div>
  );
}

// ── Separator ─────────────────────────────────────────────────────────────────
function Sep() {
  return (
    <div style={{ color: GOLD, opacity: 0.65, fontSize: 'clamp(28px, 5vw, 52px)', fontFamily: GD, lineHeight: 1, alignSelf: 'flex-start', paddingTop: 8 }}>:</div>
  );
}

export default function ComingSoonPage() {
  const [launchDate, setLaunchDate] = useState('2026-06-01T00:00:00.000Z');
  const [logoUrl,  setLogoUrl]  = useState('');
  const [email,    setEmail]    = useState('');
  const [status,   setStatus]   = useState('idle'); // idle | loading | success | error

  // Don't let Google index the placeholder, so the real pages index fresh at launch
  // instead of fighting a cached "Coming Soon" snapshot.
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch { /* gone */ } };
  }, []);
  const [errMsg,   setErrMsg]   = useState('');
  const inputRef = useRef(null);

  const { days, hours, minutes, seconds, done } = useCountdown(launchDate);

  // Load config from platform_settings
  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key,value')
      .in('key', ['coming_soon_launch_date', 'site_logo_url'])
      .then(({ data }) => {
        (data || []).forEach(r => {
          if (r.key === 'coming_soon_launch_date' && r.value) setLaunchDate(r.value);
          if (r.key === 'site_logo_url' && r.value)           setLogoUrl(r.value);
        });
      });
  }, []);

  // Inject admin-managed tracking scripts (GA4, StatCounter, ProvideSupport, etc.)
  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key,value')
      .eq('category', 'tracking')
      .then(({ data }) => {
        (data || []).forEach(({ key, value }) => {
          try {
            const { enabled, html } = JSON.parse(value || '{}');
            if (!enabled || !html?.trim()) return;
            if (document.querySelector(`[data-lwd-tracking-key="${key}"]`)) return;
            const wrap = document.createElement('div');
            wrap.innerHTML = html;
            wrap.querySelectorAll('script').forEach(src => {
              const s = document.createElement('script');
              Array.from(src.attributes).forEach(a => s.setAttribute(a.name, a.value));
              if (src.textContent?.trim()) s.textContent = src.textContent;
              s.setAttribute('data-lwd-tracking-key', key);
              document.head.appendChild(s);
            });
          } catch { /* fail silently */ }
        });
      })
      .catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrMsg('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    setErrMsg('');
    const result = await subscribeNewsletter({ email: trimmed, source: 'coming_soon' });
    if (result.ok) {
      setStatus('success');
      setEmail('');
    } else {
      setStatus('error');
      setErrMsg(result.error || 'Something went wrong, please try again.');
    }
  }

  return (
    <div style={{
      position:       'fixed',
      inset:          0,
      zIndex:         9999,
      background:     BG,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '40px 24px',
      overflowY:      'auto',
      overflowX:      'hidden',
    }}>

      {/* ── Ambient glow ── */}
      <div style={{
        position:     'absolute',
        top:          '20%',
        left:         '50%',
        transform:    'translateX(-50%)',
        width:        'min(600px, 100vw)',
        height:       'min(600px, 100vw)',
        borderRadius: '50%',
        background:   `radial-gradient(circle, ${GOLD}0d 0%, transparent 70%)`,
        pointerEvents:'none',
      }} />

      {/* ── Grain overlay ── */}
      <div style={{
        position:   'absolute',
        inset:      0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
        opacity:    0.6,
        pointerEvents:'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

        {/* ── Logo ── */}
        <div style={{ marginBottom: 40 }}>
          {logoUrl
            ? <img src={logoUrl} alt="Luxury Wedding Directory" style={{ height: 48, objectFit: 'contain' }} />
            : <div style={{ fontFamily: GD, fontSize: 22, letterSpacing: '0.18em', color: GOLD, textTransform: 'uppercase' }}>Luxury Wedding Directory</div>
          }
        </div>

        {/* ── Divider ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 44, width: '100%', maxWidth: 400 }}>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${GOLD}55)` }} />
          <span style={{ color: GOLD, fontSize: 14, opacity: 0.7 }}>◈</span>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${GOLD}55)` }} />
        </div>

        {/* ── Eyebrow ── */}
        <div style={{
          fontFamily:    NU,
          fontSize:      11,
          letterSpacing: '0.26em',
          textTransform: 'uppercase',
          color:         GOLD,
          marginBottom:  20,
          fontWeight:    600,
        }}>
          Luxury Wedding Directory
        </div>

        {/* ── Headline ── */}
        <h1 style={{
          fontFamily:  GD,
          fontSize:    'clamp(32px, 7vw, 60px)',
          fontWeight:  400,
          color:       WHITE,
          textAlign:   'center',
          lineHeight:  1.15,
          margin:      0,
          marginBottom: 16,
        }}>
          We Are{' '}
          <span style={{ color: GOLD, fontStyle: 'italic' }}>Launching</span>
          <br />Very Soon
        </h1>

        <p style={{
          fontFamily:   NU,
          fontSize:     'clamp(13px, 2vw, 15px)',
          color:        MUTED,
          textAlign:    'center',
          lineHeight:   1.7,
          margin:       '0 0 52px',
          maxWidth:     460,
          fontWeight:   300,
        }}>
          Luxury Wedding Directory is almost ready. We are putting the finishing
          touches on something extraordinary for couples planning
          exceptional weddings.
          <br /><br />Be the first to know when we go live.
        </p>

        {/* ── Countdown ── */}
        {!done ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'clamp(12px, 3vw, 28px)', marginBottom: 60 }}>
            <Digit value={days}    label="Days"    />
            <Sep />
            <Digit value={hours}   label="Hours"   />
            <Sep />
            <Digit value={minutes} label="Minutes" />
            <Sep />
            <Digit value={seconds} label="Seconds" />
          </div>
        ) : (
          <div style={{ marginBottom: 60, fontFamily: GD, fontSize: 28, color: GOLD, letterSpacing: '0.08em' }}>
            We are live ✦
          </div>
        )}

        {/* ── Email capture ── */}
        {status === 'success' ? (
          <div style={{
            padding:      '18px 28px',
            border:       `1px solid ${GOLD}44`,
            borderRadius: 4,
            background:   `${GOLD}0d`,
            fontFamily:   NU,
            fontSize:     14,
            color:        GOLD,
            letterSpacing:'0.04em',
            textAlign:    'center',
            marginBottom: 52,
          }}>
            ✦ &nbsp;You're on the list. We'll be in touch.
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{
              display:       'flex',
              gap:           0,
              width:         '100%',
              maxWidth:      480,
              marginBottom:  52,
              flexWrap:      'wrap',
              justifyContent:'center',
              gap:           8,
            }}
          >
            <input
              ref={inputRef}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email address"
              disabled={status === 'loading'}
              style={{
                flex:           '1 1 240px',
                padding:        '14px 18px',
                background:     'rgba(255,255,255,0.04)',
                border:         `1px solid ${errMsg ? '#c0392b' : 'rgba(211,154,33,0.3)'}`,
                borderRadius:   3,
                fontSize:       13,
                color:          WHITE,
                fontFamily:     NU,
                outline:        'none',
                letterSpacing:  '0.02em',
              }}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                padding:        '14px 28px',
                background:     `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
                border:         'none',
                borderRadius:   3,
                fontSize:       11,
                fontWeight:     700,
                letterSpacing:  '0.16em',
                textTransform:  'uppercase',
                color:          '#1a1409',
                cursor:         status === 'loading' ? 'wait' : 'pointer',
                whiteSpace:     'nowrap',
                fontFamily:     NU,
                opacity:        status === 'loading' ? 0.7 : 1,
              }}
            >
              {status === 'loading' ? 'Sending…' : 'Notify Me'}
            </button>
            {errMsg && (
              <div style={{ width: '100%', textAlign: 'center', fontSize: 11, color: '#e07070', fontFamily: NU, marginTop: 4 }}>
                {errMsg}
              </div>
            )}
          </form>
        )}

        {/* ── Divider ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 32, width: '100%', maxWidth: 400 }}>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, rgba(211,154,33,0.2))` }} />
          <span style={{ color: GOLD, fontSize: 10, opacity: 0.6 }}>◈</span>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, rgba(211,154,33,0.2))` }} />
        </div>

        {/* ── Sign-off ── */}
        <div style={{ fontFamily: GD, fontSize: 15, color: 'rgba(240,237,230,0.68)', letterSpacing: '0.06em', textAlign: 'center', marginBottom: 12, fontStyle: 'italic' }}>
         , The LWD Editorial Team
        </div>

        {/* ── Footer ── */}
        <div style={{ fontFamily: NU, fontSize: 11, color: 'rgba(240,237,230,0.48)', letterSpacing: '0.1em', textAlign: 'center' }}>
          © {new Date().getFullYear()} Luxury Wedding Directory. All rights reserved.
        </div>

      </div>
    </div>
  );
}
